import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Modal, Badge } from 'react-bootstrap';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const CreateAgent = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'agent'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdAgent, setCreatedAgent] = useState(null);
  const { isAdmin, currentUser } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await adminService.createAgent(formData);
      
      if (result.success) {
        setCreatedAgent(result.agent);
        setShowSuccess(true);
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          role: 'agent'
        });
      }
    } catch (err) {
      setError('Error creating agent: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    setCreatedAgent(null);
  };

  const handleViewAgents = () => {
    setShowSuccess(false);
    navigate('/admin/agents');
  };

  const handleCreateAnother = () => {
    setShowSuccess(false);
    setCreatedAgent(null);
  };

  if (!isAdmin()) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Access denied. Admin privileges required.</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Success Modal */}
      <Modal show={showSuccess} onHide={handleSuccessClose} centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>✅ {formData.role === 'admin' ? 'Admin' : 'Agent'} Created Successfully</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <h5>{formData.role === 'admin' ? 'Admin' : 'Agent'} Account Created!</h5>
            <div className="bg-light p-3 rounded mt-3">
              <p><strong>Name:</strong> {createdAgent?.firstName} {createdAgent?.lastName}</p>
              <p><strong>Email:</strong> {createdAgent?.email}</p>
              <p><strong>Role:</strong> 
                <Badge bg={formData.role === 'admin' ? 'danger' : 'primary'} className="ms-2">
                  {formData.role === 'admin' ? 'Administrator' : 'Agent'}
                </Badge>
              </p>
              <p><strong>Status:</strong> <span className="text-success">Active</span></p>
            </div>
            <p className="text-muted small mt-2">
              The {formData.role === 'admin' ? 'admin' : 'agent'} can now login using the provided credentials.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCreateAnother}>
            Create Another {formData.role === 'admin' ? 'Admin' : 'Agent'}
          </Button>
          <Button variant="primary" onClick={handleViewAgents}>
            View All Agents
          </Button>
        </Modal.Footer>
      </Modal>

      <Row className="justify-content-center">
        <Col md={8}>
          <Card className="admin-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">Create New {formData.role === 'admin' ? 'Admin' : 'Agent'}</h2>
                <Button 
                  variant="outline-primary" 
                  onClick={() => navigate('/admin/agents')}
                >
                  ← Back to Agents
                </Button>
              </div>
              
              <Alert variant="info">
                <strong>Current Admin:</strong> {currentUser?.email}
                <br />
                <small className="text-muted">You are creating a new {formData.role === 'admin' ? 'administrator' : 'agent'} account</small>
              </Alert>
              
              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder="Enter first name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Enter last name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter email address"
                  />
                  <Form.Text className="text-muted">
                    This will be used for login and notifications
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number (optional)"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Role *</Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="agent">Agent</option>
                    <option value="admin">Administrator</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {formData.role === 'admin' 
                      ? 'Administrators have full access to all system features' 
                      : 'Agents can manage their assigned leads and follow-ups'
                    }
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        minLength={6}
                        placeholder="Enter password (min 6 characters)"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Confirm Password *</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        placeholder="Confirm password"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Alert variant="warning">
                  <strong>Important:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Passwords must be at least 6 characters long</li>
                    <li>The email address must be unique and valid</li>
                    <li>{formData.role === 'admin' ? 'Admins' : 'Agents'} will receive login credentials via email</li>
                    <li>Role cannot be changed after creation</li>
                  </ul>
                </Alert>

                <div className="d-grid gap-2">
                  <Button 
                    variant={formData.role === 'admin' ? 'danger' : 'primary'} 
                    type="submit" 
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating {formData.role === 'admin' ? 'Admin' : 'Agent'}...
                      </>
                    ) : (
                      `Create ${formData.role === 'admin' ? 'Admin' : 'Agent'}`
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CreateAgent;